import React, { useEffect, useMemo, useState } from 'react';
import { X, Pencil } from 'lucide-react';
import { adminService } from '../../services/adminService';
import { useTranslation } from '../../i18n/LocaleContext';
import Toast from '../../components/Toast';

const FIELDS = [
  'enabled', 'task_count', 'reward_min', 'reward_max', 'reward_total',
  'lucky_positions', 'lucky_amounts', 'lucky_payouts', 'topup_target',
  'withdraw_min', 'withdraw_max', 'auto_payout', 'remark'
];

const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const range = (min, max) => `${money(min)} - ${money(max)}`;

const AdminNewbiePlan = () => {
  const { t } = useTranslation();
  const [plans, setPlans] = useState([]);
  const [luckyStats, setLuckyStats] = useState([]);
  const [newUsers, setNewUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [luckyReview, setLuckyReview] = useState([]);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewBusy, setReviewBusy] = useState(false);

  const loadReview = async () => {
    try {
      setReviewLoading(true);
      const data = await adminService.getLuckyOrdersReview();
      setLuckyReview(data.items || []);
    } catch (error) {
      setToast({ type: 'error', message: error.message });
    } finally {
      setReviewLoading(false);
    }
  };

  const handleLuckyApprove = async (order) => {
    if (!window.confirm(t('aNewbieLuckyApproveConfirm', { user: order.username, payout: money(order.payout) }))) return;
    try {
      setReviewBusy(true);
      await adminService.approveLuckyOrder(order.id);
      setToast({ type: 'success', message: t('aNewbieLuckyApproved') });
      await Promise.all([loadReview(), load()]);
    } catch (error) {
      setToast({ type: 'error', message: error.message });
    } finally {
      setReviewBusy(false);
    }
  };

  const handleLuckyReject = async (order) => {
    if (!window.confirm(t('aNewbieLuckyRejectConfirm', { user: order.username, amount: money(order.amount) }))) return;
    try {
      setReviewBusy(true);
      await adminService.rejectLuckyOrder(order.id, 'admin reject');
      setToast({ type: 'success', message: t('aNewbieLuckyRejected') });
      await Promise.all([loadReview(), load()]);
    } catch (error) {
      setToast({ type: 'error', message: error.message });
    } finally {
      setReviewBusy(false);
    }
  };

  const load = async () => {
    try {
      setLoading(true);
      const data = await adminService.getNewbiePlan();
      setPlans(data.plans || []);
      setLuckyStats(data.luckyStats || []);
      setNewUsers(data.newUsers || []);
    } catch (error) {
      setToast({ type: 'error', message: error.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); loadReview(); }, []);

  const statByDay = useMemo(() => {
    const map = {};
    luckyStats.forEach((s) => { map[Number(s.plan_day)] = s; });
    return map;
  }, [luckyStats]);

  const usersByDay = useMemo(() => {
    const map = {};
    newUsers.forEach((s) => { map[Number(s.day_no)] = Number(s.users); });
    return map;
  }, [newUsers]);

  const openEdit = (plan) => {
    setEditing(plan);
    const next = {};
    FIELDS.forEach((f) => { next[f] = plan[f] === null || plan[f] === undefined ? '' : String(plan[f]); });
    setForm(next);
  };

  const submit = async (event) => {
    event.preventDefault();
    try {
      setSaving(true);
      await adminService.updateNewbiePlan(editing.id, {
        ...form,
        enabled: form.enabled === '1' ? 1 : 0,
        auto_payout: form.auto_payout === '1' ? 1 : 0
      });
      setEditing(null);
      setToast({ type: 'success', message: t('aNewbieSaved') });
      await load();
    } catch (error) {
      setToast({ type: 'error', message: error.message });
    } finally {
      setSaving(false);
    }
  };

  const field = (key, label, extra = {}) => (
    <div>
      <label className="mb-2 block text-sm font-medium text-gray-300">{label}</label>
      <input
        type={extra.type || 'text'}
        step={extra.step}
        min={extra.min}
        value={form[key] ?? ''}
        placeholder={extra.placeholder}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        className="w-full rounded-lg border border-gray-600 bg-gray-900 px-4 py-2 text-white outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
      />
      {extra.hint && <p className="mt-1 text-xs text-gray-500">{extra.hint}</p>}
    </div>
  );

  const th = 'px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase whitespace-nowrap';

  return (
    <div>
      {toast && <Toast type={toast.type} message={toast.message} onClose={() => setToast(null)} />}

      <div className="mb-6">
        <h1 className="text-3xl font-bold text-white">{t('aNewbieTitle')}</h1>
        <p className="text-gray-400 mt-1">{t('aNewbieSubtitle')}</p>
      </div>

      {/* Lucky orders paid by users, waiting for manual payout review */}
      <div className="mb-6 bg-gray-800 rounded-xl shadow-sm overflow-hidden border border-gray-700">
        <div className="px-6 py-4 border-b border-gray-700 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white">{t('aNewbieLuckyReviewTitle')}</h2>
          {luckyReview.length > 0 && (
            <span className="text-sm text-gray-400">{luckyReview.length}</span>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-900 border-b border-gray-700">
              <tr>
                <th className={th}>ID</th>
                <th className={th}>{t('aNewbieLuckyColUser')}</th>
                <th className={th}>{t('aNewbieColDay')}</th>
                <th className={th}>{t('aNewbieLuckyColAmount')}</th>
                <th className={th}>{t('aNewbieLuckyColPayout')}</th>
                <th className={th}>{t('aNewbieLuckyColPaidAt')}</th>
                <th className={th}>{t('aNewbieActions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {reviewLoading ? (
                <tr>
                  <td colSpan="7" className="px-6 py-6 text-center text-gray-400">{t('aNewbieLoading')}</td>
                </tr>
              ) : luckyReview.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-6 text-center text-gray-400">{t('aNewbieLuckyReviewEmpty')}</td>
                </tr>
              ) : luckyReview.map((order) => (
                <tr key={order.id} className="hover:bg-gray-700">
                  <td className="px-6 py-4 text-sm text-gray-300">#{order.id}</td>
                  <td className="px-6 py-4">
                    <p className="text-sm font-medium text-white">{order.username}</p>
                    <p className="text-xs text-gray-400">{order.phone}</p>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-300">{t('aNewbieDay', { day: order.plan_day })} · #{order.position}</td>
                  <td className="px-6 py-4 text-sm text-gray-300">{money(order.amount)}</td>
                  <td className="px-6 py-4 text-sm font-semibold text-white">{money(order.payout)}</td>
                  <td className="px-6 py-4 text-sm text-gray-300">
                    {order.paid_at ? new Date(order.paid_at).toLocaleString() : '-'}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleLuckyApprove(order)}
                        disabled={reviewBusy}
                        className="px-3 py-1 bg-green-500 text-white rounded-lg text-xs hover:bg-green-600 disabled:opacity-50"
                      >
                        {t('aNewbieLuckyApprove')}
                      </button>
                      <button
                        onClick={() => handleLuckyReject(order)}
                        disabled={reviewBusy}
                        className="px-3 py-1 bg-red-500 text-white rounded-lg text-xs hover:bg-red-600 disabled:opacity-50"
                      >
                        {t('aNewbieLuckyReject')}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-gray-800 rounded-xl shadow-sm overflow-hidden border border-gray-700">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-900 border-b border-gray-700">
              <tr>
                <th className={th}>{t('aNewbieColDay')}</th>
                <th className={th}>{t('aNewbieTaskCount')}</th>
                <th className={th}>{t('aNewbieRewardRange')}</th>
                <th className={th}>{t('aNewbieRewardTotal')}</th>
                <th className={th}>{t('aNewbieLuckyPositions')}</th>
                <th className={th}>{t('aNewbieTopupTarget')}</th>
                <th className={th}>{t('aNewbieAutoPayout')}</th>
                <th className={th}>{t('aNewbieColNewUsers')}</th>
                <th className={th}>{t('aNewbieLuckyStats')}</th>
                <th className={th}>{t('aNewbieStatus')}</th>
                <th className={th}>{t('aNewbieActions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan="11" className="px-6 py-8 text-center text-gray-400">{t('aNewbieLoading')}</td>
                </tr>
              ) : plans.length === 0 ? (
                <tr>
                  <td colSpan="11" className="px-6 py-8 text-center text-gray-400">{t('aNewbieEmpty')}</td>
                </tr>
              ) : plans.map((plan) => {
                const stat = statByDay[plan.day_no] || {};
                const lucky = String(plan.lucky_positions || '').split(',').map((s) => s.trim()).filter(Boolean);
                return (
                  <tr key={plan.id} className="hover:bg-gray-700">
                    <td className="px-6 py-4">
                      <p className="text-sm font-medium text-white">{t('aNewbieDay', { day: plan.day_no })}</p>
                      {plan.remark && <p className="text-xs text-gray-400 mt-0.5">{plan.remark}</p>}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-300">{plan.task_count}</td>
                    <td className="px-6 py-4 text-sm text-gray-300">{range(plan.reward_min, plan.reward_max)}</td>
                    <td className="px-6 py-4 text-sm font-semibold text-white">{money(plan.reward_total)}</td>
                    <td className="px-6 py-4 text-sm text-gray-300">
                      {lucky.length ? lucky.map((p, i) => (
                        <div key={p}>#{p} {money(plan.lucky_amounts?.split(',')[i])} → {money(plan.lucky_payouts?.split(',')[i])}</div>
                      )) : '-'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-300">{Number(plan.topup_target) > 0 ? money(plan.topup_target) : '-'}</td>
                    <td className="px-6 py-4 text-sm text-gray-300">{plan.auto_payout ? `${money(plan.withdraw_min)} - ${money(plan.withdraw_max)}` : '-'}</td>
                    <td className="px-6 py-4 text-sm text-gray-300">{t('aNewbieUsers', { count: usersByDay[plan.day_no] || 0 })}</td>
                    <td className="px-6 py-4 text-sm text-gray-300">
                      {Number(stat.total || 0) > 0
                        ? `${stat.total} · ${t('aNewbieLuckyPaid')} ${stat.paid || 0} · ${t('aNewbieLuckyInReview')} ${stat.in_review || 0} · ${t('aNewbieLuckyPending')} ${stat.pending || 0}`
                        : t('aNewbieLuckyNone')}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${
                        plan.enabled
                          ? 'bg-green-900 bg-opacity-30 border border-green-700 text-green-400'
                          : 'bg-gray-700 border border-gray-600 text-gray-300'
                      }`}>
                        {plan.enabled ? t('aNewbieOn') : t('aNewbieOff')}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => openEdit(plan)}
                        className="px-3 py-1 bg-gray-700 hover:bg-gray-600 text-white rounded-lg text-xs inline-flex items-center gap-1"
                      >
                        <Pencil className="w-3 h-3" />
                        {t('aNewbieEdit')}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <form onSubmit={submit} className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-2xl border border-gray-700 bg-gray-800 p-6 shadow-2xl">
            <div className="mb-5 flex items-start justify-between">
              <h2 className="text-xl font-bold text-white">
                {t('aNewbieEdit')} · {t('aNewbieDay', { day: editing.day_no })}
              </h2>
              <button
                type="button"
                onClick={() => !saving && setEditing(null)}
                className="rounded-lg p-1 text-gray-400 hover:bg-gray-700 hover:text-white"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {field('task_count', t('aNewbieTaskCount'), { type: 'number', step: '1', min: '0' })}
              {field('reward_total', t('aNewbieRewardTotal'), { type: 'number', step: '0.01', min: '0' })}
              {field('reward_min', t('aNewbieRewardRange'), { type: 'number', step: '0.01', min: '0' })}
              {field('reward_max', ' / max', { type: 'number', step: '0.01', min: '0' })}
              {field('lucky_positions', t('aNewbieLuckyPositions'), { placeholder: '20,40', hint: t('aNewbieHintPositions') })}
              {field('lucky_amounts', t('aNewbieLuckyAmounts'), { placeholder: '20,30' })}
              {field('lucky_payouts', t('aNewbieLuckyPayouts'), { placeholder: '22,33', hint: t('aNewbieHintPayouts') })}
              {field('topup_target', t('aNewbieTopupTarget'), { type: 'number', step: '0.01', min: '0' })}
              {field('withdraw_min', t('aNewbieWithdrawRange'), { type: 'number', step: '0.01', min: '0' })}
              {field('withdraw_max', ' / max', { type: 'number', step: '0.01', min: '0' })}
            </div>

            <div className="mt-4 space-y-3">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-300">
                <input type="checkbox" checked={form.enabled === '1'} onChange={(e) => setForm({ ...form, enabled: e.target.checked ? '1' : '0' })} className="h-4 w-4 rounded" />
                {t('aNewbieEnabledLabel')}
              </label>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-300">
                <input type="checkbox" checked={form.auto_payout === '1'} onChange={(e) => setForm({ ...form, auto_payout: e.target.checked ? '1' : '0' })} className="h-4 w-4 rounded" />
                {t('aNewbieAutoPayout')}
              </label>
              {field('remark', t('aNewbieRemark'))}
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button type="button" onClick={() => setEditing(null)} disabled={saving} className="rounded-lg bg-gray-700 px-4 py-2 font-medium text-white hover:bg-gray-600 disabled:opacity-50">
                {t('aNewbieCancel')}
              </button>
              <button type="submit" disabled={saving} className="rounded-lg bg-brand-500 px-4 py-2 font-medium text-white hover:bg-brand-600 disabled:opacity-50">
                {saving ? t('aNewbieSaving') : t('aNewbieSave')}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default AdminNewbiePlan;
